import { useState, useCallback } from 'react'
import { parsePdf, ParsedPdf } from '../services/pdf/parser'
import { extractCVFromTextBasic, convertAIResponseToExtractedCV, ExtractedCV } from '../services/pdf/extractor'
import { parsePdfWithAI } from '../services/api/claude'
import { validatePdfFile } from '../utils/validation'
import { useTranslation } from '../contexts/LanguageContext'
import type { CV } from '../types/cv'

export type ImportStep = 'upload' | 'parsing' | 'analyzing' | 'review' | 'complete' | 'error'

interface UsePdfImportReturn {
  step: ImportStep
  progress: number
  error: string | null
  parsedPdf: ParsedPdf | null
  extractedCV: ExtractedCV | null
  usedAI: boolean
  aiExtractionId: string | null
  rateLimited: boolean
  importPdf: (file: File, userId?: string) => Promise<void>
  reset: () => void
  getImportedCV: () => CV | null
}

// Check if error is a Firebase rate limit error
function isRateLimitError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'code' in error) {
    return (error as { code: string }).code === 'functions/resource-exhausted'
  }
  return false
}

export function usePdfImport(): UsePdfImportReturn {
  const { t } = useTranslation()
  const [step, setStep] = useState<ImportStep>('upload')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [parsedPdf, setParsedPdf] = useState<ParsedPdf | null>(null)
  const [extractedCV, setExtractedCV] = useState<ExtractedCV | null>(null)
  const [usedAI, setUsedAI] = useState(false)
  const [aiExtractionId, setAiExtractionId] = useState<string | null>(null)
  const [rateLimited, setRateLimited] = useState(false)

  const reset = useCallback(() => {
    setStep('upload')
    setProgress(0)
    setError(null)
    setParsedPdf(null)
    setExtractedCV(null)
    setUsedAI(false)
    setAiExtractionId(null)
    setRateLimited(false)
  }, [])

  const importPdf = useCallback(async (file: File, _userId?: string) => {
    // Validate file
    const validation = validatePdfFile(file)
    if (!validation.valid) {
      const isWrongType = file.type !== 'application/pdf'
      setError(isWrongType ? t.import.fileMustBePdf : `${t.import.fileTooLarge} 10MB`)
      setStep('error')
      return
    }

    setStep('parsing')
    setProgress(10)
    setError(null)
    setUsedAI(false)
    setAiExtractionId(null)

    try {
      // Parse PDF to extract text
      setProgress(30)
      const parsed = await parsePdf(file)
      setParsedPdf(parsed)
      setProgress(50)

      // Check if text was extracted
      if (!parsed.fullText || parsed.fullText.length < 50) {
        setError(t.import.noTextExtracted)
        setStep('error')
        return
      }

      // Try AI extraction first
      setStep('analyzing')
      setProgress(60)
      let extracted: ExtractedCV

      // Start progress simulation during AI analysis (60% -> 89%)
      let currentProgress = 60
      const progressInterval = setInterval(() => {
        if (currentProgress < 89) {
          currentProgress += 1
          setProgress(currentProgress)
        }
      }, 500)

      try {
        const aiResponse = await parsePdfWithAI(parsed.fullText, {
          language: 'es',
          fileName: file.name,
          fileSize: file.size,
        })
        clearInterval(progressInterval)
        extracted = convertAIResponseToExtractedCV(aiResponse)
        setUsedAI(true)
        setProgress(90)
        // Note: AI extraction is saved by the Cloud Function (for both auth and anonymous users)
      } catch (aiError) {
        clearInterval(progressInterval)
        // Check if it's a rate limit error
        if (isRateLimitError(aiError)) {
          console.warn('Rate limit reached:', aiError)
          setRateLimited(true)
        }
        // AI failed, fallback to basic regex extraction
        console.warn('AI extraction failed, using fallback:', aiError)
        extracted = extractCVFromTextBasic(parsed.fullText)
        setProgress(90)
      }

      setExtractedCV(extracted)
      setProgress(100)
      setStep('review')
    } catch (err) {
      const message = err instanceof Error ? err.message : t.import.errorTitle
      setError(message)
      setStep('error')
    }
  }, [t])

  const getImportedCV = useCallback((): CV | null => {
    if (!extractedCV?.cv) return null

    // Merge with empty CV structure to ensure all fields exist
    return {
      personalInfo: {
        fullName: extractedCV.cv.personalInfo?.fullName || '',
        title: extractedCV.cv.personalInfo?.title || '',
        email: extractedCV.cv.personalInfo?.email || '',
        phone: extractedCV.cv.personalInfo?.phone || '',
        location: extractedCV.cv.personalInfo?.location || '',
        links: extractedCV.cv.personalInfo?.links || [],
      },
      summary: extractedCV.cv.summary || '',
      experience: extractedCV.cv.experience || [],
      education: extractedCV.cv.education || [],
      skills: {
        technical: extractedCV.cv.skills?.technical || [],
        soft: extractedCV.cv.skills?.soft || [],
        tools: extractedCV.cv.skills?.tools || [],
      },
      languages: extractedCV.cv.languages || [],
      certifications: extractedCV.cv.certifications || [],
      projects: extractedCV.cv.projects || [],
      settings: {
        // Set the detected language from AI extraction
        language: extractedCV.detectedLanguage,
      },
    }
  }, [extractedCV])

  return {
    step,
    progress,
    error,
    parsedPdf,
    extractedCV,
    usedAI,
    aiExtractionId,
    rateLimited,
    importPdf,
    reset,
    getImportedCV,
  }
}
